import { useEffect, useRef } from "react";

/**
 * Attempts to keep the screen awake using the Screen Wake Lock API.
 * Falls back silently on unsupported browsers.
 */
export function useWakeLock() {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    const isSupported = "wakeLock" in navigator;
    if (!isSupported) {
      console.warn("Screen Wake Lock API is not supported on this device.");
      return;
    }

    const requestWakeLock = async () => {
      try {
        wakeLockRef.current = await (navigator as any).wakeLock.request("screen");
        console.log("Wake lock acquired.");
      } catch (err: any) {
        console.warn("Failed to acquire wake lock:", err.message);
      }
    };

    requestWakeLock();

    // Re-acquire wake lock when the document becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
        console.log("Wake lock released.");
      }
    };
  }, []);
}
